/**
 * THE HIRE LOG SHEET — 0214. The register, and one month in detail.
 *
 * A machine on hire is paid for the days it is on site. Every day the Site
 * Engineer marks each machine:
 *
 *     W  worked
 *     I  idle — the machine and its operator were ready, OVAL had no work
 *     H  Sunday or holiday, the machine still on site
 *     B  breakdown, or the operator absent
 *     ·  blank — not on site
 *
 * W, I and H are paid; B is not. At the end of the month he submits the sheet
 * and the Resident Construction Manager certifies it — the same two signatures
 * as a Measurement Book page, and the same rule that the one who recorded it
 * may not certify it. A certified sheet cannot be changed. Its paid days are
 * what the next running account bill picks up, at the day rate on the order
 * (a monthly rate divided by 30).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, CheckCircle2, Clock, Plus, RotateCcw, Save, Send, TriangleAlert, Truck, Undo2, XCircle,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, usePeople, useQuery, useSites } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime, qty, rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Loading, Modal, SearchInput, Select, StatusPill,
  Textarea, useToast,
} from '@/components/ui'
import { AuditTrail } from '@/components/docs/shared'
import { serviceCan } from '../nav'
import { HIRE_LIVE, OpenHireLogModal, monthLabel, quotedLabel } from '../components/WoHire'

const n = (v: unknown) => Number(v ?? 0) || 0

type Mark = '' | 'W' | 'I' | 'H' | 'B'
const CYCLE: Mark[] = ['', 'W', 'I', 'H', 'B']
const PAID: Mark[] = ['W', 'I', 'H']

const MARK_CLS: Record<Mark, string> = {
  '': 'border-slate-200 bg-white text-slate-300',
  W: 'border-emerald-300 bg-emerald-100 text-emerald-800',
  I: 'border-sky-300 bg-sky-100 text-sky-800',
  H: 'border-slate-300 bg-slate-200 text-slate-700',
  B: 'border-red-300 bg-red-100 text-red-800',
}

const MARK_WORD: Record<Mark, string> = {
  '': 'Not on site', W: 'Worked', I: 'Idle — OVAL had no work',
  H: 'Sunday / holiday', B: 'Breakdown — not paid',
}

/** Today in the browser's own calendar, as YYYY-MM-DD. */
function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// THE REGISTER
// ---------------------------------------------------------------------------
export function HireLogList() {
  const { profile } = useAuth()
  const people = usePeople()
  const { data: allSites } = useSites()
  const [term, setTerm] = useState('')
  const [status, setStatus] = useState('')
  const [opening, setOpening] = useState(false)

  const { data: rows, loading } = useQuery<any>('wo_hire_logs',
    () => supabase.from('wo_hire_logs')
      .select('*, work_orders(title, vendors(company_name))')
      .order('period_month', { ascending: false }),
    [], { cacheable: false })

  const { data: dayRows } = useQuery<any>('wo_hire_log_days_paid',
    () => supabase.from('wo_hire_log_days').select('id, log_id, day_status'),
    [], { cacheable: false })

  const { data: orders } = useQuery<any>('hire_orders_live',
    () => supabase.from('work_orders').select('id, title, site_id, status, vendors(company_name)')
      .eq('contract_nature', 'EQUIPMENT_HIRE').in('status', HIRE_LIVE).order('id'),
    [], { cacheable: false })

  // 0220: a sheet of items hired by the piece has challans, not machine-days.
  const { data: moveIds } = useQuery<any>('wo_hire_moves_count',
    () => supabase.from('wo_hire_moves').select('id, log_id'),
    [], { cacheable: false })
  const challans = useMemo(() => {
    const m: Record<string, number> = {}
    for (const x of moveIds as any[]) m[x.log_id] = (m[x.log_id] ?? 0) + 1
    return m
  }, [moveIds])

  const paid = useMemo(() => {
    const m: Record<string, number> = {}
    for (const d of dayRows as any[]) {
      if (PAID.includes(d.day_status)) m[d.log_id] = (m[d.log_id] ?? 0) + 1
    }
    return m
  }, [dayRows])

  const siteName = (id: string) => (allSites as any[]).find((s) => s.id === id)?.site_name ?? id

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase()
    return (rows as any[]).filter((r) => {
      if (status && r.status !== status) return false
      if (t && ![r.id, r.wo_id, r.work_orders?.title, r.work_orders?.vendors?.company_name,
                 siteName(r.site_id)]
        .some((v) => String(v ?? '').toLowerCase().includes(t))) return false
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, term, status, allSites])

  const waiting = (rows as any[]).filter((r) => r.status === 'SUBMITTED').length
  const canRecord = serviceCan(profile?.role, 'recordMeasurement')

  if (loading) return <Loading label="Opening the hire log sheets…" />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Hire Log Sheets</h1>
          <p className="text-sm text-slate-500">
            {(rows as any[]).length} sheet{(rows as any[]).length === 1 ? '' : 's'}
            {waiting > 0 && <> · <span className="font-semibold text-amber-700">
              {waiting} awaiting certification</span></>}
          </p>
        </div>
        {canRecord && (
          <button className="btn-primary" onClick={() => setOpening(true)}
                  disabled={(orders as any[]).length === 0}
                  title={(orders as any[]).length === 0 ? 'No approved equipment hire order on your sites yet' : undefined}>
            <Plus className="h-4 w-4" /> Open a log sheet
          </button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Truck className="mr-1 inline h-3.5 w-3.5" />
        One sheet per equipment hire order per month. The <strong>Site Engineer</strong> marks
        every machine every day; the <strong>Resident Construction Manager</strong> certifies the
        sheet. Worked, idle and Sunday/holiday days are paid, breakdown days are not. Only a
        certified sheet reaches the running account bill.
      </div>

      <Card title={null}>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchInput value={term} onChange={setTerm}
                       placeholder="Sheet, work order, contractor or site…"
                       className="min-w-[200px] flex-1" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-44" placeholder="Any status"
                  options={[...new Set((rows as any[]).map((r) => r.status))].sort()
                    .map((s) => ({ value: s, label: s }))} />
        </div>

        {shown.length === 0 ? (
          <EmptyState icon={<Truck className="h-8 w-8" />}
                      title={(rows as any[]).length ? 'Nothing matches' : 'No hire log sheets yet'}
                      message={(rows as any[]).length
                        ? 'Clear the search or the status to see them all.'
                        : 'A sheet is opened for each month an approved equipment hire order has machines on site.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Sheet</th><th>Work order</th><th>Site</th><th>Month</th>
                  <th className="num">Paid machine-days</th><th>Recorded by</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/hire-logs/${r.id}`}
                            className="text-oval-700 hover:underline">{r.id}</Link>
                    </td>
                    <td className="whitespace-nowrap">
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="text-oval-700 hover:underline">{r.wo_id}</Link>
                      <span className="block max-w-[180px] truncate text-[11px] text-slate-400">
                        {r.work_orders?.vendors?.company_name}
                      </span>
                    </td>
                    <td className="max-w-[160px] truncate">{siteName(r.site_id)}</td>
                    <td className="whitespace-nowrap">{monthLabel(r.period_month)}</td>
                    <td className="num">
                      {paid[r.id] ?? 0}
                      {challans[r.id] ? (
                        <span className="block text-[11px] text-slate-400">
                          {challans[r.id]} challan{challans[r.id] === 1 ? '' : 's'}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-[140px] truncate text-xs">
                      {people.name({ id: r.recorded_by, email: r.recorder_email })}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <OpenHireLogModal open={opening} onClose={() => setOpening(false)} woId={null}
                        orders={(orders as any[]).map((o) => ({
                          id: o.id,
                          label: `${o.id} — ${o.vendors?.company_name ?? ''} · ${siteName(o.site_id)}`,
                        }))} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// ONE MONTH
// ---------------------------------------------------------------------------
interface Cell { status: Mark; hours: string }
/** 0220: one delivery (IN) or return (OUT) of pieces, as typed. */
interface Move {
  key: string; wo_line_id: string; move_date: string; direction: 'IN' | 'OUT'
  pieces: string; challan_ref: string; remark: string
}
const key = (line: string, machine: number, day: string) => `${line}|${machine}|${day}`

export function HireLogDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const people = usePeople()
  const navigate = useNavigate()
  const toast = useToast()

  const [cells, setCells] = useState<Record<string, Cell>>({})
  const [remarks, setRemarks] = useState('')
  const [dirty, setDirty] = useState(false)
  const [hoursMode, setHoursMode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState<null | 'return' | 'cancel' | 'uncertify'>(null)
  const [reason, setReason] = useState('')

  const { data: logs, loading, refresh } = useQuery<any>(`hire_log_${id}`,
    () => supabase.from('wo_hire_logs')
      .select('*, work_orders(id, title, status, start_date, completion_date, vendors(company_name)), sites(site_name)')
      .eq('id', id),
    [id], { cacheable: false })
  const log = (logs as any[])[0]

  const { data: lines, refresh: refreshLines } = useQuery<any>(`hire_log_lines_${id}`,
    () => supabase.from('wo_line_items')
      .select('id, sl_no, description, rate, quantity, qty_measured, hire_units, '
              + 'hire_rate_basis, hire_quoted_rate, with_operator, fuel_by_oval, '
              + 'work_items(hire_by_pieces)')
      .eq('wo_id', log?.wo_id ?? '__none__').eq('pricing_basis', 'EQUIPMENT_HIRE').order('sl_no'),
    [log?.wo_id], { cacheable: false, enabled: Boolean(log?.wo_id) })

  const { data: progress } = useQuery<any>(`hire_log_allowed_${id}`,
    () => supabase.from('v_wo_line_progress').select('wo_line_id, qty_allowed')
      .eq('wo_id', log?.wo_id ?? '__none__'),
    [log?.wo_id], { cacheable: false, idField: 'wo_line_id', enabled: Boolean(log?.wo_id) })

  const { data: days, refresh: refreshDays } = useQuery<any>(`hire_log_days_${id}`,
    () => supabase.from('wo_hire_log_days').select('*').eq('log_id', id),
    [id], { cacheable: false })

  // What is on the server becomes the grid, whenever it is re-read.
  useEffect(() => {
    const c: Record<string, Cell> = {}
    for (const d of days as any[]) {
      c[key(d.wo_line_id, d.machine_no, d.day)] = {
        status: d.day_status as Mark, hours: d.hours == null ? '' : String(d.hours),
      }
    }
    setCells(c)
    setDirty(false)
  }, [days])
  useEffect(() => { setRemarks(log?.remarks ?? '') }, [log?.remarks])

  // The calendar of this sheet's month.
  const month = useMemo(() => {
    if (!log?.period_month) return [] as { iso: string; d: number; sunday: boolean }[]
    const [y, m] = String(log.period_month).slice(0, 7).split('-').map(Number)
    const count = new Date(y, m, 0).getDate()
    return Array.from({ length: count }, (_, i) => {
      const d = i + 1
      return {
        iso: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        d, sunday: new Date(y, m - 1, d).getDay() === 0,
      }
    })
  }, [log?.period_month])
  const today = todayIso()

  // 0220: items hired by the piece are not on the machine grid - they have
  // their own deliveries-and-returns section below.
  const machineLines = useMemo(() => (lines as any[]).filter((l) => !l.work_items?.hire_by_pieces), [lines])
  const pieceLines = useMemo(() => (lines as any[]).filter((l) => l.work_items?.hire_by_pieces), [lines])

  const rowsOf = useMemo(() => machineLines.flatMap((l) =>
    Array.from({ length: Math.max(n(l.hire_units), 1) }, (_, i) => ({ line: l, machine: i + 1 }))),
  [machineLines])

  // ---- 0220: deliveries and returns -------------------------------------
  const { data: moveRows, refresh: refreshMoves } = useQuery<any>(`hire_log_moves_${id}`,
    () => supabase.from('wo_hire_moves').select('*').eq('log_id', id).order('move_date'),
    [id], { cacheable: false })
  const { data: pieceSheet, refresh: refreshPieceSheet } = useQuery<any>(`hire_log_pieces_${id}`,
    () => supabase.rpc('fn_hire_piece_sheet', { p_log_id: id }) as any,
    [id, log?.status], { cacheable: false, idField: 'wo_line_id', enabled: pieceLines.length > 0 })
  const [moves, setMoves] = useState<Move[]>([])
  useEffect(() => {
    setMoves((moveRows as any[]).map((m) => ({
      key: m.id, wo_line_id: m.wo_line_id, move_date: m.move_date, direction: m.direction,
      pieces: String(m.pieces), challan_ref: m.challan_ref ?? '', remark: m.remark ?? '',
    })))
  }, [moveRows])
  const setMove = (key: string, patch: Partial<Move>) => {
    setMoves((ms) => ms.map((m) => (m.key === key ? { ...m, ...patch } : m))); setDirty(true)
  }

  const role = profile?.role
  const editable = Boolean(log) && ['DRAFT', 'RETURNED'].includes(log.status)
    && serviceCan(role, 'recordMeasurement')
  const canCertify = Boolean(log) && log.status === 'SUBMITTED'
    && serviceCan(role, 'certifyMeasurement') && log.recorded_by !== profile?.id

  // ---- arithmetic -------------------------------------------------------
  function countRow(line: string, machine: number) {
    const c = { W: 0, I: 0, H: 0, B: 0 }
    for (const day of month) {
      const s = cells[key(line, machine, day.iso)]?.status
      if (s) c[s as 'W' | 'I' | 'H' | 'B'] += 1
    }
    return { ...c, paid: c.W + c.I + c.H }
  }
  const paidOfLine = (lineId: string, units: number) =>
    Array.from({ length: Math.max(units, 1) }, (_, i) => countRow(lineId, i + 1).paid)
      .reduce((a, b) => a + b, 0)
  const allowed = (lineId: string) =>
    n((progress as any[]).find((p) => p.wo_line_id === lineId)?.qty_allowed)

  // 0220: a piece line's piece-days for this month, as the database worked
  // them out at the last save (fn_hire_piece_sheet).
  const pieceDaysOf = (lineId: string) =>
    n((pieceSheet as any[]).find((s) => s.wo_line_id === lineId)?.piece_days)
  const earnedOf = (l: any) => (l.work_items?.hire_by_pieces
    ? pieceDaysOf(l.id) : paidOfLine(l.id, n(l.hire_units)))

  const totals = useMemo(() => {
    let paidDays = 0; let pieceDays = 0; let value = 0
    for (const l of lines as any[]) {
      const p = earnedOf(l)
      if (l.work_items?.hire_by_pieces) pieceDays += p; else paidDays += p
      value += Math.round(p * n(l.rate) * 100) / 100
    }
    return { paidDays, pieceDays, value }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, cells, month, pieceSheet])

  /** Lines this sheet would take past the days ordered plus variations. */
  const overruns = useMemo(() => (lines as any[]).map((l) => {
    const thisSheet = earnedOf(l)
    // qty_measured already includes this sheet once it is certified.
    const before = n(l.qty_measured) - (log?.status === 'CERTIFIED' ? thisSheet : 0)
    return { l, over: before + thisSheet - allowed(l.id) }
  }).filter((x) => x.over > 0.0005),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [lines, cells, progress, log?.status, month, pieceSheet])

  // ---- editing ----------------------------------------------------------
  function cycle(line: string, machine: number, day: string) {
    if (!editable || day > today) return
    setCells((c) => {
      const k = key(line, machine, day)
      const cur = c[k]?.status ?? ''
      const next = CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length]
      const copy = { ...c }
      if (next === '') delete copy[k]
      else copy[k] = { status: next, hours: c[k]?.hours ?? '' }
      return copy
    })
    setDirty(true)
  }

  function setHours(line: string, machine: number, day: string, hours: string) {
    setCells((c) => {
      const k = key(line, machine, day)
      if (!c[k]) return c
      return { ...c, [k]: { ...c[k], hours } }
    })
    setDirty(true)
  }

  /** Fill the blanks up to today: Sundays as holiday, the rest as worked. */
  function fillRow(line: string, machine: number) {
    setCells((c) => {
      const copy = { ...c }
      for (const day of month) {
        if (day.iso > today) continue
        const k = key(line, machine, day.iso)
        if (!copy[k]) copy[k] = { status: day.sunday ? 'H' : 'W', hours: '' }
      }
      return copy
    })
    setDirty(true)
  }

  function clearRow(line: string, machine: number) {
    setCells((c) => {
      const copy = { ...c }
      for (const day of month) delete copy[key(line, machine, day.iso)]
      return copy
    })
    setDirty(true)
  }

  async function save(quiet = false): Promise<boolean> {
    setBusy(true)
    try {
      const payload = Object.entries(cells).map(([k, v]) => {
        const [wo_line_id, machine, day] = k.split('|')
        return {
          wo_line_id, machine_no: Number(machine), day, status: v.status,
          hours: v.hours.trim() === '' ? null : Number(v.hours),
        }
      })
      const { error } = await supabase.rpc('rpc_hire_log_save',
        { p_log_id: id, p_days: payload, p_remarks: remarks })
      if (error) throw new Error(dbMessage(error, 'The sheet could not be saved.'))
      // 0220: the challans, in the same press of Save.
      if (pieceLines.length > 0) {
        const bad = moves.find((m) => !m.wo_line_id || !m.move_date || !(Number(m.pieces) > 0))
        if (bad) throw new Error('Every delivery or return needs an item, a date and a number of pieces.')
        const { error: mErr } = await supabase.rpc('rpc_hire_moves_save', {
          p_log_id: id,
          p_moves: moves.map((m) => ({
            wo_line_id: m.wo_line_id, move_date: m.move_date, direction: m.direction,
            pieces: Math.round(Number(m.pieces)), challan_ref: m.challan_ref, remark: m.remark,
          })),
        })
        if (mErr) throw new Error(dbMessage(mErr, 'The deliveries and returns could not be saved.'))
      }
      if (!quiet) toast.success('Saved.')
      await refreshDays(); await refresh(); await refreshMoves(); await refreshPieceSheet()
      setDirty(false)
      return true
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The sheet could not be saved.')
      return false
    } finally { setBusy(false) }
  }

  async function act(fn: string, args: Record<string, unknown>, done: string) {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc(fn, args)
      if (error) throw new Error(dbMessage(error, 'That did not go through.'))
      toast.success(typeof data === 'string' ? data : done)
      setAsking(null); setReason('')
      await refresh(); await refreshDays(); await refreshLines(); await refreshPieceSheet()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That did not go through.')
    } finally { setBusy(false) }
  }

  async function submit() {
    if (dirty && !(await save(true))) return
    await act('rpc_hire_log_submit', { p_log_id: id }, 'Submitted.')
  }

  if (loading) return <Loading label="Opening the hire log sheet…" />
  if (!log) {
    return <EmptyState icon={<Truck className="h-8 w-8" />} title="Not found"
                       message="No hire log sheet with that number, or it belongs to a site you cannot see." />
  }

  const askCopy = {
    return: { title: 'Send it back to the engineer', min: 1, fn: 'rpc_hire_log_return',
              hint: 'Say what is wrong, so it is not marked the same way again.' },
    cancel: { title: 'Cancel this sheet', min: 5, fn: 'rpc_hire_log_cancel',
              hint: 'For a sheet opened by mistake. It is kept on the record as cancelled, never deleted.' },
    uncertify: { title: 'Withdraw the certification', min: 10, fn: 'rpc_hire_log_uncertify',
                 hint: 'Only possible while no running account bill has been raised on this order since it was certified. The sheet goes back to the engineer.' },
  } as const

  return (
    <div className="space-y-4">
      <Link to="/service/hire-logs"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> All hire log sheets
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">{log.id}</h1>
          <p className="text-sm text-slate-600">
            {monthLabel(log.period_month)} ·{' '}
            <Link to={`/service/work-orders/${log.wo_id}`} className="text-oval-700 hover:underline">
              {log.wo_id}
            </Link>
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {log.sites?.site_name} · {log.work_orders?.vendors?.company_name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={log.status} />
          {editable && (
            <>
              <button className="btn-secondary btn-sm" disabled={busy || !dirty}
                      onClick={() => void save()}>
                <Save className="h-4 w-4" /> Save
              </button>
              <button className="btn-primary btn-sm"
                      disabled={busy || (Object.keys(cells).length === 0 && moves.length === 0
                        && !(pieceSheet as any[]).some((s) => n(s.opening) > 0))}
                      onClick={() => void submit()}>
                <Send className="h-4 w-4" /> Submit for certification
              </button>
              <button className="btn-ghost btn-sm text-red-600" disabled={busy}
                      onClick={() => setAsking('cancel')}>
                <XCircle className="h-4 w-4" /> Cancel sheet
              </button>
            </>
          )}
          {canCertify && (
            <>
              <button className="btn-primary btn-sm" disabled={busy || overruns.length > 0}
                      onClick={() => void act('rpc_hire_log_certify', { p_log_id: id }, 'Certified.')}>
                <CheckCircle2 className="h-4 w-4" /> Certify
              </button>
              <button className="btn-secondary btn-sm" disabled={busy}
                      onClick={() => setAsking('return')}>
                <RotateCcw className="h-4 w-4" /> Return
              </button>
            </>
          )}
          {log.status === 'CERTIFIED' && serviceCan(role, 'certifyMeasurement') && (
            <button className="btn-ghost btn-sm text-slate-600" disabled={busy}
                    onClick={() => setAsking('uncertify')}>
              <Undo2 className="h-4 w-4" /> Withdraw certification
            </button>
          )}
        </div>
      </div>

      {log.status === 'RETURNED' && log.return_reason && (
        <Alert tone="warning" title="Sent back for correction">{log.return_reason}</Alert>
      )}
      {log.status === 'SUBMITTED' && serviceCan(role, 'certifyMeasurement')
        && log.recorded_by === profile?.id && (
        <Alert tone="info" title="You recorded this sheet">
          It has to be certified by a different Resident Construction Manager.
        </Alert>
      )}
      {log.status === 'CANCELLED' && (
        <Alert tone="info" title="Cancelled">{log.cancel_reason ?? 'No reason was recorded.'}</Alert>
      )}
      {overruns.length > 0 && (
        <Alert tone="warning" title="This sheet goes past the days ordered">
          {overruns.map(({ l, over }) => (
            <span key={l.id} className="block">
              Line {l.sl_no}, {l.description}: {qty(over)} day(s) over.
            </span>
          ))}
          <span className="mt-1 block">
            It cannot be certified until a Variation Order for the extra days is approved on{' '}
            <Link to={`/service/work-orders/${log.wo_id}`} className="font-semibold underline">{log.wo_id}</Link>.
          </span>
        </Alert>
      )}

      {/* ---------- 0220: items hired by the piece ---------- */}
      {pieceLines.length > 0 && (
        <Card title="Deliveries and returns"
              subtitle={editable
                ? 'One row per challan. Rent runs from the delivery day to the return day, both included. Pieces still on site carry into next month. Save as you go.'
                : 'As recorded on the challans. Rent runs from the delivery day to the return day, both included.'}
              actions={editable ? (
                <button className="btn-secondary btn-sm" onClick={() => {
                  setMoves((ms) => [...ms, {
                    key: Math.random().toString(36).slice(2),
                    wo_line_id: pieceLines.length === 1 ? pieceLines[0].id : '',
                    move_date: month[0]?.iso ?? '', direction: 'IN',
                    pieces: '', challan_ref: '', remark: '',
                  }]); setDirty(true)
                }}>
                  <Plus className="h-4 w-4" /> Add a challan
                </button>
              ) : undefined}>
          {moves.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500">
              No delivery or return this month.
              {(pieceSheet as any[]).some((s) => n(s.opening) > 0)
                ? ' Pieces carried in from last month still earn rent for every day of it.'
                : editable ? ' Add a row for each delivery or return challan.' : ''}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Date</th><th>Item</th><th>Delivered / returned</th>
                    <th className="num">Pieces</th><th>Challan no.</th><th>Remark</th>
                    {editable && <th aria-label="Remove" />}
                  </tr>
                </thead>
                <tbody>
                  {moves.map((m) => (
                    <tr key={m.key}>
                      <td>
                        <input type="date" className="field w-36" value={m.move_date} disabled={!editable}
                               min={month[0]?.iso} max={month[month.length - 1]?.iso && month[month.length - 1].iso < today ? month[month.length - 1].iso : today}
                               onChange={(e) => setMove(m.key, { move_date: e.target.value })} />
                      </td>
                      <td>
                        <select className="field min-w-[200px]" value={m.wo_line_id} disabled={!editable}
                                onChange={(e) => setMove(m.key, { wo_line_id: e.target.value })}>
                          <option value="">Choose…</option>
                          {pieceLines.map((l) => (
                            <option key={l.id} value={l.id}>{l.sl_no}. {l.description}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select className="field w-32" value={m.direction} disabled={!editable}
                                onChange={(e) => setMove(m.key, { direction: e.target.value as 'IN' | 'OUT' })}>
                          <option value="IN">Delivered</option>
                          <option value="OUT">Returned</option>
                        </select>
                      </td>
                      <td className="num">
                        <input type="number" min="1" step="1" className="field w-24 text-right"
                               value={m.pieces} disabled={!editable}
                               onChange={(e) => setMove(m.key, { pieces: e.target.value })} />
                      </td>
                      <td>
                        <input className="field w-32" value={m.challan_ref} disabled={!editable}
                               placeholder="DC-001" onChange={(e) => setMove(m.key, { challan_ref: e.target.value })} />
                      </td>
                      <td>
                        <input className="field min-w-[140px]" value={m.remark} disabled={!editable}
                               onChange={(e) => setMove(m.key, { remark: e.target.value })} />
                      </td>
                      {editable && (
                        <td>
                          <button className="btn-ghost btn-sm text-red-600" aria-label="Remove this challan"
                                  onClick={() => { setMoves((ms) => ms.filter((x) => x.key !== m.key)); setDirty(true) }}>
                            <XCircle className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
            This month, item by item {dirty && editable && <span className="font-normal normal-case text-amber-800">— save to update</span>}
          </p>
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th><th>Item</th><th className="num">On site at start</th>
                  <th className="num">Delivered</th><th className="num">Returned</th>
                  <th className="num">On site at end</th><th className="num">Piece-days</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(pieceSheet as any[]).map((s) => (
                  <tr key={s.wo_line_id}>
                    <td>{s.sl_no}</td>
                    <td className="max-w-[240px]">
                      {s.description}
                      <span className="block text-[11px] text-slate-400">{rupees(s.rate)} a piece a day</span>
                    </td>
                    <td className="num">{qty(s.opening)}</td>
                    <td className="num">{qty(s.delivered)}</td>
                    <td className="num">{qty(s.returned)}</td>
                    <td className={n(s.closing) < 0 ? 'num font-semibold text-red-700' : 'num'}>{qty(s.closing)}</td>
                    <td className="num font-semibold">{qty(s.piece_days)}</td>
                    <td className="num whitespace-nowrap">{rupees(Math.round(n(s.piece_days) * n(s.rate) * 100) / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {log.period_month && today <= (month[month.length - 1]?.iso ?? '') && (
            <p className="mt-2 text-xs text-slate-500">
              <Clock className="mr-1 inline h-3.5 w-3.5" />
              Piece-days are worked out to the end of {monthLabel(log.period_month)}, assuming nothing more
              goes back. The sheet can be submitted once the month is over.
            </p>
          )}
        </Card>
      )}

      <Card title={machineLines.length > 0 ? 'The month, machine by machine' : 'This month'}
            subtitle={machineLines.length === 0
              ? 'Totals and remarks for the sheet.'
              : editable
              ? 'Tap a day to change it: blank → W → I → H → B → blank. Days after today cannot be marked. Save as you go.'
              : 'As recorded. W worked, I idle, H Sunday/holiday, B breakdown, blank not on site.'}
            actions={machineLines.length > 0 ? (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
                       checked={hoursMode} onChange={(e) => setHoursMode(e.target.checked)} />
                Show hour-meter hours
              </label>
            ) : undefined}>
        {machineLines.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2 text-[11px]">
          {(['W', 'I', 'H', 'B'] as Mark[]).map((m) => (
            <span key={m} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 ${MARK_CLS[m]}`}>
              <strong>{m}</strong> {MARK_WORD[m]}
            </span>
          ))}
        </div>
        )}

        {rowsOf.length === 0 ? null : (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-[180px] bg-white px-2 py-1 text-left font-semibold text-slate-600">
                    Machine
                  </th>
                  {month.map((day) => (
                    <th key={day.iso}
                        className={`w-8 px-0.5 py-1 text-center font-medium ${day.sunday ? 'text-red-700' : 'text-slate-500'}`}>
                      {day.d}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-right font-semibold text-slate-600">Paid</th>
                  <th className="px-2 py-1 text-right font-semibold text-slate-600">B</th>
                  <th className="px-2 py-1 text-right font-semibold text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rowsOf.map(({ line, machine }) => {
                  const c = countRow(line.id, machine)
                  return (
                    <tr key={`${line.id}-${machine}`} className="border-t border-slate-100">
                      <td className="sticky left-0 z-10 bg-white px-2 py-1 align-top">
                        <span className="block font-medium text-slate-800">
                          {line.sl_no}. {line.description}
                          {n(line.hire_units) > 1 && <span className="text-slate-500"> — no. {machine}</span>}
                        </span>
                        <span className="block text-[11px] text-slate-400">
                          {quotedLabel(line)} · {rupees(line.rate)} a day
                        </span>
                        {editable && (
                          <span className="mt-1 flex gap-2">
                            <button type="button" className="btn-ghost btn-sm px-1 py-0.5 text-[11px] text-oval-700"
                                    onClick={() => fillRow(line.id, machine)}
                                    title="Blank days up to today: Sundays as H, other days as W">
                              Fill blanks
                            </button>
                            <button type="button" className="btn-ghost btn-sm px-1 py-0.5 text-[11px] text-slate-500"
                                    onClick={() => clearRow(line.id, machine)}>
                              Clear
                            </button>
                          </span>
                        )}
                      </td>
                      {month.map((day) => {
                        const cell = cells[key(line.id, machine, day.iso)]
                        const mark: Mark = cell?.status ?? ''
                        const future = day.iso > today
                        if (hoursMode && mark) {
                          return (
                            <td key={day.iso} className="px-0.5 py-1 text-center">
                              <input type="number" min="0" max="24" step="0.5"
                                     className={`h-7 w-10 rounded border px-0.5 text-center text-[11px] ${MARK_CLS[mark]}`}
                                     value={cell?.hours ?? ''} disabled={!editable}
                                     title={`${MARK_WORD[mark]} — hours (information only)`}
                                     onChange={(e) => setHours(line.id, machine, day.iso, e.target.value)} />
                            </td>
                          )
                        }
                        return (
                          <td key={day.iso} className="px-0.5 py-1 text-center">
                            <button type="button"
                                    className={`h-7 w-7 rounded border text-[11px] font-semibold ${MARK_CLS[mark]}
                                                ${future ? 'cursor-not-allowed opacity-30' : ''}
                                                ${!editable ? 'cursor-default' : ''}`}
                                    disabled={!editable || future}
                                    title={`${day.d}: ${MARK_WORD[mark]}${cell?.hours ? ` · ${cell.hours} h` : ''}`}
                                    onClick={() => cycle(line.id, machine, day.iso)}>
                              {mark || '·'}
                            </button>
                          </td>
                        )
                      })}
                      <td className="px-2 py-1 text-right font-semibold tabular-nums">{c.paid}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-red-700">{c.B || ''}</td>
                      <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                        {rupees(Math.round(c.paid * n(line.rate) * 100) / 100)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-slate-50 px-4 py-3">
          <p className="text-sm text-slate-600">
            {machineLines.length > 0 && <>Paid machine-days on this sheet: <strong className="tabular-nums">{totals.paidDays}</strong></>}
            {machineLines.length > 0 && pieceLines.length > 0 && ' · '}
            {pieceLines.length > 0 && <>Piece-days: <strong className="tabular-nums">{qty(totals.pieceDays)}</strong></>}
          </p>
          <p className="text-sm text-slate-600">
            At the day rates on the order: <strong className="tabular-nums text-slate-900">{rupees(totals.value)}</strong>
            <span className="ml-1 text-xs text-slate-400">before GST and deductions</span>
          </p>
        </div>
        {dirty && editable && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-800">
            <Clock className="h-3.5 w-3.5" /> Changes not saved yet.
          </p>
        )}

        <Field label="Remarks" className="mt-4"
               hint="Anything the certifier should know — a machine replaced, a breakdown reported late.">
          <Textarea rows={2} value={remarks} disabled={!editable}
                    onChange={(e) => { setRemarks(e.target.value); setDirty(true) }} />
        </Field>
      </Card>

      <Card title="Signatures">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
          <div>
            <dt className="text-xs font-medium text-slate-500">Recorded by</dt>
            <dd>{people.name({ id: log.recorded_by, email: log.recorder_email })}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500">Certified by</dt>
            <dd>{log.certified_by
              ? <>{people.name({ id: log.certified_by, email: log.certifier_email })}
                  <span className="block text-xs text-slate-400">{dateTime(log.certified_at)}</span></>
              : '—'}</dd>
          </div>
        </dl>
        {!editable && !['CERTIFIED', 'CANCELLED'].includes(log.status) && !canCertify && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <TriangleAlert className="h-3.5 w-3.5" />
            Waiting for the Resident Construction Manager.
          </p>
        )}
      </Card>

      <Modal open={asking !== null} onClose={() => { setAsking(null); setReason('') }}
             title={asking ? askCopy[asking].title : ''}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => { setAsking(null); setReason('') }}>
                   Back
                 </button>
                 <button className={asking === 'return' ? 'btn-primary' : 'btn-danger'}
                         disabled={busy || !asking || reason.trim().length < askCopy[asking].min}
                         onClick={() => asking && void act(askCopy[asking].fn,
                           { p_log_id: id, p_reason: reason.trim() }, 'Done.')}>
                   {busy ? 'Working…' : 'Confirm'}
                 </button>
               </>
             }>
        {asking && (
          <Field label="Reason" required hint={askCopy[asking].hint}>
            <Textarea rows={3} value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
      </Modal>

      <AuditTrail docType="HIRE" docId={log.id} />

      {log.status === 'CANCELLED' && (
        <button className="btn-ghost btn-sm text-slate-600" onClick={() => navigate('/service/hire-logs')}>
          Back to the register
        </button>
      )}
    </div>
  )
}
